/**
 * The Shop sheet's state (docs/plans/012-shop.md): what is for sale with exact prices, what the account owns, and the way to the payment page.
 * Whether the player may buy comes from the consent state; ownership is handed to the game, which unlocks the items. No countdowns, no
 * "last chance": the list is the same every time.
 */
import type { ShopActions, ShopView } from "../ui/view-model";
import { AccountError, type AccountApi, type ShopItem } from "./api";

const codeOf = (error: unknown): string => (error instanceof AccountError ? error.code : "server_error");
const priceLabel = (i: ShopItem) => `${i.currency.toUpperCase()} ${(i.amountCents / 100).toFixed(2)}`;
/** The payment confirmation can reach the server after the browser is back: look at 0, 1, 2 and 4 seconds. */
const LOOK_DELAYS = [1000, 1000, 2000];

export interface ShopDeps {
  api: AccountApi;
  isSignedIn(): boolean;
  onChange(): void;
  /** Leaves the game for the payment page (`window.location.assign`). */
  navigate(url: string): void;
  /** From the consent state: may this player buy, and if not what is missing. */
  gate(): { loaded: boolean; canBuy: boolean; missing: ("google" | "parent")[] };
  loadGate(): Promise<unknown>;
  /** Tells the game what the account owns. */
  onOwned(ids: readonly string[]): void;
  /** Whether the game wants this item sold now (it is locked for the shop and not owned). */
  offered(id: string): boolean;
  wait(ms: number): Promise<void>;
}

export function createShop(deps: ShopDeps) {
  let loaded = false;
  let testMode = false;
  let items: ShopItem[] = [];
  let owned: string[] = [];
  let result: "done" | "cancelled" | null = null;
  let confirming = false;
  let problem: string | null = null;
  let busy = false;
  let gift: string | null = null;

  const setOwned = (ids: string[]) => {
    owned = [...ids].sort();
    deps.onOwned(owned);
  };

  async function run(work: () => Promise<void>): Promise<string | null> {
    busy = true;
    problem = null;
    deps.onChange();
    try {
      await work();
      return null;
    } catch (error) {
      problem = codeOf(error);
      return problem;
    } finally {
      busy = false;
      deps.onChange();
    }
  }

  const api = {
    view(): ShopView {
      const signedIn = deps.isSignedIn();
      const gate = deps.gate();
      const have = new Set(signedIn ? owned : []);
      return {
        signedIn,
        loaded,
        testMode,
        items: items.filter((i) => have.has(i.id) || deps.offered(i.id)).map((i) => ({ id: i.id, name: i.name, description: i.description, priceLabel: priceLabel(i), owned: have.has(i.id) })),
        canBuy: signedIn && gate.loaded && gate.canBuy,
        missing: signedIn ? gate.missing : [],
        result,
        confirming,
        problem,
        busy,
        gift,
      };
    },

    /** The list works signed out; ownership and the gate need an account. */
    load: () =>
      run(async () => {
        const shop = await deps.api.shop();
        items = shop.items;
        testMode = shop.testMode;
        if (deps.isSignedIn()) {
          setOwned(await deps.api.purchases());
          await deps.loadGate();
        }
        loaded = true;
      }),

    /** Quietly fetches what the account owns (after sign-in or at page start). */
    async refreshOwned(): Promise<void> {
      if (!deps.isSignedIn()) return;
      try {
        setOwned(await deps.api.purchases());
      } catch {
        // Offline or signed out meanwhile: the items stay as they were.
      }
      deps.onChange();
    },

    buy: async (id: string): Promise<string | null> => {
      if (!deps.isSignedIn()) return "not_signed_in";
      const gate = deps.gate();
      if (gate.loaded && !gate.canBuy) {
        problem = "purchase_not_allowed";
        deps.onChange();
        return problem;
      }
      return run(async () => {
        deps.navigate(await deps.api.checkout(id));
      });
    },

    /** A promo code gives the item for free: no Google or parent step (a gift costs nothing). */
    redeem: async (code: string): Promise<string | null> => {
      if (!deps.isSignedIn()) return "not_signed_in";
      gift = null;
      return run(async () => {
        const { itemId } = await deps.api.redeemPromo(code.trim());
        setOwned([...new Set([...owned, itemId])]);
        gift = itemId;
      });
    },

    /** The way back from the payment page (`?purchase=done|cancelled`). */
    async setResult(raw: string): Promise<void> {
      if (raw !== "done" && raw !== "cancelled") return;
      result = raw;
      confirming = raw === "done" && deps.isSignedIn();
      deps.onChange();
      if (!confirming) return;
      try {
        for (let look = 0; ; look++) {
          const now = [...(await deps.api.purchases())].sort();
          const changed = now.join("|") !== owned.join("|");
          setOwned(now);
          if (changed || look >= LOOK_DELAYS.length) break;
          await deps.wait(LOOK_DELAYS[look]!);
        }
      } catch {
        // The items will show up the next time the Shop opens.
      } finally {
        confirming = false;
        deps.onChange();
      }
    },

    dismissResult() {
      result = null;
      deps.onChange();
    },

    /** Forget everything signed-in: sign-out, account deletion. The game locks the items again. */
    clear() {
      loaded = false;
      owned = [];
      deps.onOwned([]);
      result = null;
      confirming = false;
      problem = null;
      busy = false;
      gift = null;
      deps.onChange();
    },
  };
  return api;
}

export type Shop = ReturnType<typeof createShop>;

export function shopActions(shop: Shop): ShopActions {
  return {
    onLoadShop: async () => void (await shop.load()),
    onBuy: async (id) => void (await shop.buy(id)),
    onRedeem: async (code) => void (await shop.redeem(code)),
    onDismissShopResult: () => shop.dismissResult(),
  };
}

/** Reads `?purchase=done|cancelled` and gives the address to put in the bar instead (null when there was nothing to remove). */
export function parsePurchaseResult(search: string, pathname: string, hash: string): { result: "done" | "cancelled" | null; cleaned: string | null } {
  const params = new URLSearchParams(search);
  if (!params.has("purchase")) return { result: null, cleaned: null };
  const raw = params.get("purchase");
  params.delete("purchase");
  const rest = params.toString();
  return { result: raw === "done" || raw === "cancelled" ? raw : null, cleaned: `${pathname}${rest ? `?${rest}` : ""}${hash}` };
}
